import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  type CSSProperties,
  type DragEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Badge, Button, Card, Input, Spinner } from '@/components/admin/ui';
import {
  EmptyState,
} from '@/components';
import {
  BroadcastJobStatusPanel,
} from '@/components/admin/BroadcastJobProgress';
import type { AdminStickerPack } from '@/lib/admin-types';
import { apiJson, getApiErrorMessage } from '@/lib/api';
import type {
  CreatePushBroadcastRes,
  PushBroadcastJob,
} from '@/lib/push-broadcast-types';
import { pushDataFromDeeplink, pushDeeplinkPath } from '@/lib/push-deeplinks';

export const Route = createFileRoute('/admin/sticker-packs/$id')({
  component: StickerPackDetailPage,
});

// Stickers are shown up to ~320pt wide on 3x displays in the app, so 1024px
// is the minimum size that stays crisp everywhere.
const RECOMMENDED_DIMENSION = 1024;
const MAX_RECOMMENDED_BYTES = 500 * 1024;
const ACCEPTED_IMAGE_TYPES = new Set([
  'image/png',
  'image/webp',
  'image/jpeg',
  'image/gif',
]);

type ImageInfo = {
  width: number;
  height: number;
  /** null when detection isn't possible (e.g. canvas is unavailable). */
  hasTransparency: boolean | null;
};

/**
 * Samples the image at low resolution and looks for any non-opaque pixel.
 * Catches stickers exported with their transparency flattened to a solid
 * background. For GIFs this checks the first frame only.
 */
function detectTransparency(img: HTMLImageElement): boolean | null {
  try {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(img, 0, 0, size, size);
    const data = ctx.getImageData(0, 0, size, size).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        return true;
      }
    }
    return false;
  } catch {
    return null;
  }
}

function isAcceptedImageFile(file: File): boolean {
  return ACCEPTED_IMAGE_TYPES.has(file.type);
}

/** Detect APNG via the ``acTL`` chunk so we don't flatten it in a canvas. */
async function isAnimatedPngFile(file: File): Promise<boolean> {
  if (file.type !== 'image/png' && !file.name.toLowerCase().endsWith('.png')) {
    // Procreate sometimes names APNGs ``.gif`` while the bytes are still PNG.
    if (file.type !== 'image/gif' && !file.name.toLowerCase().endsWith('.gif')) {
      return false;
    }
  }
  const probeSize = Math.min(file.size, 1024 * 1024);
  const buf = new Uint8Array(await file.slice(0, probeSize).arrayBuffer());
  if (
    buf.length < 8 ||
    buf[0] !== 0x89 ||
    buf[1] !== 0x50 ||
    buf[2] !== 0x4e ||
    buf[3] !== 0x47
  ) {
    return false;
  }
  // Chunk layout: length(4) + type(4) + data(length) + crc(4)
  let offset = 8;
  while (offset + 8 <= buf.length) {
    const length =
      ((buf[offset]! << 24) |
        (buf[offset + 1]! << 16) |
        (buf[offset + 2]! << 8) |
        buf[offset + 3]!) >>>
      0;
    const type = String.fromCharCode(
      buf[offset + 4]!,
      buf[offset + 5]!,
      buf[offset + 6]!,
      buf[offset + 7]!,
    );
    if (type === 'acTL') {
      return true;
    }
    if (type === 'IDAT' || type === 'IEND') {
      return false;
    }
    offset += 12 + length;
  }
  return false;
}

function pickImageFileFromDataTransfer(dataTransfer: DataTransfer): File | null {
  const file = Array.from(dataTransfer.files).find(isAcceptedImageFile);
  if (file) {
    return file;
  }

  const item = Array.from(dataTransfer.items).find(
    (entry) => entry.kind === 'file' && ACCEPTED_IMAGE_TYPES.has(entry.type),
  );
  return item?.getAsFile() ?? null;
}

// Highest first; we step down until the result fits under MAX_RECOMMENDED_BYTES.
const COMPRESSION_QUALITIES = [0.85, 0.75, 0.6] as const;

// Checkerboard backdrop so missing transparency is obvious in previews.
const CHECKERBOARD_STYLE: CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
};

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Prepares a static image for upload: pads non-square art to a square with
 * transparent space, and re-encodes oversized files to PNG (downscaling to
 * the recommended dimension). Animated files (GIF / APNG) are returned
 * untouched — the server square-pads and converts them to an optimized GIF,
 * since canvas re-encoding would drop the animation. Falls back to the
 * original file when processing fails.
 */
async function compressStickerImage(file: File): Promise<File> {
  if (file.type === 'image/gif' || (await isAnimatedPngFile(file))) {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  try {
    const needsCompression = file.size > MAX_RECOMMENDED_BYTES;
    const needsPadding = bitmap.width !== bitmap.height;
    if (!needsCompression && !needsPadding) {
      return file;
    }

    const sourceSide = Math.max(bitmap.width, bitmap.height);
    // Only sacrifice resolution when we're shrinking the file anyway.
    const targetSide = needsCompression ? Math.min(sourceSide, RECOMMENDED_DIMENSION) : sourceSide;
    const scale = targetSide / sourceSide;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetSide;
    canvas.height = targetSide;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return file;
    }
    ctx.drawImage(
      bitmap,
      Math.round((targetSide - width) / 2),
      Math.round((targetSide - height) / 2),
      width,
      height,
    );

    let best: Blob | null = null;
    for (const quality of COMPRESSION_QUALITIES) {
      const blob = await canvasToBlob(canvas, 'image/png', quality);
      if (!blob) {
        break;
      }
      if (!best || blob.size < best.size) {
        best = blob;
      }
      if (blob.size <= MAX_RECOMMENDED_BYTES) {
        break;
      }
    }

    if (!best) {
      return file;
    }
    // Padding changes the geometry, so we must keep the re-encoded version
    // even if it ends up larger; pure compression should only win on size.
    if (!needsPadding && best.size >= file.size) {
      return file;
    }

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'sticker';
    return new File([best], `${baseName}.png`, { type: 'image/png' });
  } finally {
    bitmap.close();
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Basename without extension, used to prefill the sticker name input. */
function stickerNameFromFilename(filename: string): string {
  return filename.replace(/\.[^.]+$/, '').trim() || 'sticker';
}

function getStickerWarnings(
  file: File,
  info: ImageInfo | null,
  isAnimatedUpload: boolean,
): string[] {
  const warnings: string[] = [];
  if (info) {
    if (info.width !== info.height) {
      warnings.push(
        `Image is not square (${info.width}×${info.height}). It will be padded to a square with transparent space ${isAnimatedUpload ? 'on the server' : 'before upload'}.`,
      );
    }
    if (Math.max(info.width, info.height) < RECOMMENDED_DIMENSION) {
      warnings.push(
        `Image is smaller than the recommended ${RECOMMENDED_DIMENSION}×${RECOMMENDED_DIMENSION}px and may look blurry in the app's full-size preview.`,
      );
    }
    if (info.hasTransparency === false && file.type !== 'image/jpeg') {
      warnings.push(
        'No transparent pixels detected — the background appears solid. Stickers look best with transparent backgrounds; double-check the export settings.',
      );
    }
  }
  if (file.size > MAX_RECOMMENDED_BYTES) {
    if (isAnimatedUpload) {
      warnings.push(
        `File is ${formatBytes(file.size)} — larger than the recommended ${formatBytes(MAX_RECOMMENDED_BYTES)}. It will be converted to an optimized GIF on the server (downscaled and re-encoded, animation preserved).`,
      );
    } else {
      warnings.push(
        `File is ${formatBytes(file.size)} — larger than the recommended ${formatBytes(MAX_RECOMMENDED_BYTES)}. It will be compressed automatically before upload.`,
      );
    }
  }
  if (file.type === 'image/jpeg') {
    warnings.push(
      'JPEGs have no transparency, so the sticker will have a solid background. A transparent PNG or WebP is recommended.',
    );
  }
  return warnings;
}

function StickerPackDetailPage() {
  const { id } = Route.useParams();
  const packId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stickerName, setStickerName] = useState('');
  /** True once the admin has typed in the name field for this pending upload. */
  const nameTouchedRef = useRef(false);
  const suggestAbortRef = useRef<AbortController | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageInfo, setImageInfo] = useState<ImageInfo | null>(null);
  const [isAnimatedUpload, setIsAnimatedUpload] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<'idle' | 'compressing' | 'uploading'>('idle');
  const uploading = uploadPhase !== 'idle';
  const [suggestingName, setSuggestingName] = useState(false);
  const [suggestNameError, setSuggestNameError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [publishPushPackName, setPublishPushPackName] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingFile) {
      setPreviewUrl(null);
      setImageInfo(null);
      setIsAnimatedUpload(false);
      return;
    }
    const url = URL.createObjectURL(pendingFile);
    setPreviewUrl(url);

    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) {
        setImageInfo({
          width: img.naturalWidth,
          height: img.naturalHeight,
          hasTransparency: detectTransparency(img),
        });
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setImageInfo(null);
      }
    };
    img.src = url;

    void (async () => {
      const animated =
        pendingFile.type === 'image/gif' || (await isAnimatedPngFile(pendingFile));
      if (!cancelled) {
        setIsAnimatedUpload(animated);
      }
    })();

    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
    };
  }, [pendingFile]);

  const canSubmit = Boolean(stickerName.trim() && pendingFile && !uploading);

  const detailQ = useQuery({
    queryKey: ['admin', 'sticker-pack', packId],
    queryFn: () => apiJson<AdminStickerPack>(`/v1/admin/sticker-packs/${packId}`),
    enabled: Number.isFinite(packId),
  });

  const savePack = useMutation({
    mutationFn: (body: Partial<Pick<AdminStickerPack, 'name' | 'slug' | 'sort_order' | 'is_published'>>) =>
      apiJson<AdminStickerPack>(`/v1/admin/sticker-packs/${packId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-pack', packId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-packs'] });
    },
  });

  const deletePack = useMutation({
    mutationFn: () =>
      apiJson<void>(`/v1/admin/sticker-packs/${packId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-packs'] });
      void navigate({ to: '/admin/sticker-packs' });
    },
  });

  const deleteSticker = useMutation({
    mutationFn: (stickerId: number) =>
      apiJson<void>(`/v1/admin/stickers/${stickerId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-pack', packId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-packs'] });
    },
  });

  const updateSticker = useMutation({
    mutationFn: (args: { id: number; name?: string; sort_order?: number }) =>
      apiJson(`/v1/admin/stickers/${args.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: args.name, sort_order: args.sort_order }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-pack', packId] });
    },
  });

  useEffect(() => {
    if (!pendingFile || !Number.isFinite(packId)) {
      suggestAbortRef.current?.abort();
      suggestAbortRef.current = null;
      setSuggestingName(false);
      setSuggestNameError(null);
      return;
    }

    // Only auto-suggest when the name field is still empty / untouched.
    if (nameTouchedRef.current || stickerName.trim()) {
      return;
    }

    const controller = new AbortController();
    suggestAbortRef.current?.abort();
    suggestAbortRef.current = controller;
    setSuggestingName(true);
    setSuggestNameError(null);

    void (async () => {
      try {
        const form = new FormData();
        form.append('file', pendingFile);
        const result = await apiJson<{ name: string }>(
          `/v1/admin/sticker-packs/${packId}/stickers/suggest-name`,
          { method: 'POST', body: form, signal: controller.signal },
        );
        if (controller.signal.aborted || nameTouchedRef.current) {
          return;
        }
        const suggested = result.name.trim();
        if (suggested) {
          setStickerName(suggested);
        }
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setSuggestNameError(
          getApiErrorMessage(err, 'Could not suggest a name. Type one manually.'),
        );
      } finally {
        if (!controller.signal.aborted) {
          setSuggestingName(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
    // stickerName intentionally omitted: we only auto-suggest on new file when empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- suggest once per pending file
  }, [pendingFile, packId]);

  function handleFileSelected(file: File) {
    setUploadError(null);
    setSuggestNameError(null);
    // New file: re-suggest unless the admin typed a name themselves.
    if (!nameTouchedRef.current) {
      setStickerName('');
      nameTouchedRef.current = false;
    }
    setPendingFile(file);
    setStickerName(stickerNameFromFilename(file.name));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function clearPendingSticker() {
    suggestAbortRef.current?.abort();
    suggestAbortRef.current = null;
    setPendingFile(null);
    setUploadError(null);
    setSuggestNameError(null);
    setSuggestingName(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  async function handleSubmitSticker() {
    const name = stickerName.trim();
    if (!name || !pendingFile) {
      return;
    }

    setUploadError(null);
    setUploadPhase('compressing');
    try {
      const fileToUpload = await compressStickerImage(pendingFile);

      setUploadPhase('uploading');
      const form = new FormData();
      form.append('file', fileToUpload);
      form.append('name', name);

      await apiJson(`/v1/admin/sticker-packs/${packId}/stickers/upload`, {
        method: 'POST',
        body: form,
      });

      setStickerName('');
      nameTouchedRef.current = false;
      clearPendingSticker();
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-pack', packId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'sticker-packs'] });
    } catch (err) {
      setUploadError(getApiErrorMessage(err, 'Upload failed. Try again.'));
    } finally {
      setUploadPhase('idle');
    }
  }

  if (detailQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600">
          {(detailQ.error as Error)?.message ?? 'Pack not found'}
        </p>
        <Button variant="secondary" onClick={() => void navigate({ to: '/admin/sticker-packs' })}>
          All packs
        </Button>
      </div>
    );
  }

  const pack = detailQ.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button variant="secondary" onClick={() => void navigate({ to: '/admin/sticker-packs' })}>
            ← All packs
          </Button>
          <h1 className="mt-3 text-xl font-semibold tracking-tight">{pack.name}</h1>
          <p className="text-sm text-nova-muted-foreground">{pack.slug}</p>
        </div>
        <Badge variant={pack.is_published ? 'success' : 'neutral'}>
          {pack.is_published ? 'Published' : 'Draft'}
        </Badge>
      </div>

      <PackSettingsCard
        pack={pack}
        saving={savePack.isPending}
        onSave={(body) => {
          const shouldOfferPush = !pack.is_published && body.is_published === true;
          savePack.mutate(body, {
            onSuccess: (saved) => {
              if (shouldOfferPush) {
                setPublishPushPackName(saved.name);
              }
            },
          });
        }}
        onRequestDelete={() => setDeleteConfirmOpen(true)}
      />

      {publishPushPackName ? (
        <PublishPushDialog
          packName={publishPushPackName}
          onClose={() => setPublishPushPackName(null)}
        />
      ) : null}

      {deleteConfirmOpen ? (
        <DeletePackDialog
          packName={pack.name}
          stickerCount={pack.stickers.length}
          deleting={deletePack.isPending}
          onCancel={() => setDeleteConfirmOpen(false)}
          onConfirm={() => deletePack.mutate()}
        />
      ) : null}

      <Card className="space-y-4">
        <h2 className="text-sm font-medium text-nova-foreground">Add sticker</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-sm">
            <span className="text-nova-muted-foreground">Name</span>
            <Input
              value={stickerName}
              onChange={(e) => {
                nameTouchedRef.current = true;
                setStickerName(e.target.value);
                setSuggestNameError(null);
              }}
              placeholder={suggestingName ? 'Suggesting name…' : 'Sticker name'}
              className="mt-1 max-w-xs"
            />
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/webp,image/jpeg,image/gif"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelected(file);
            }}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Choose image
          </Button>
          <Button
            type="button"
            disabled={!canSubmit}
            onClick={() => void handleSubmitSticker()}
          >
            {uploadPhase === 'compressing'
              ? 'Compressing…'
              : uploadPhase === 'uploading'
                ? 'Adding…'
                : 'Add sticker'}
          </Button>
          {pendingFile ? (
            <Button
              type="button"
              variant="secondary"
              disabled={uploading}
              onClick={clearPendingSticker}
            >
              Clear
            </Button>
          ) : null}
        </div>
        <StickerImageDropZone
          disabled={uploading}
          onFileSelected={handleFileSelected}
          onInvalidFile={() =>
            setUploadError('Please drop a PNG, WebP, JPEG, GIF, or animated PNG image.')
          }
          onChooseClick={() => fileInputRef.current?.click()}
        >
          {previewUrl && pendingFile ? (
            <div className="flex items-start gap-4">
              <img
                src={previewUrl}
                alt={stickerName.trim() || 'Sticker preview'}
                className="h-32 w-32 rounded-lg object-contain"
                style={CHECKERBOARD_STYLE}
              />
              <div className="min-w-0 flex-1 text-sm text-nova-muted-foreground">
                <p className="font-medium text-nova-foreground">Preview</p>
                <p className="mt-1 truncate">{pendingFile.name}</p>
                <p className="mt-1 text-xs text-nova-muted-foreground">
                  {imageInfo ? `${imageInfo.width}×${imageInfo.height}px · ` : ''}
                  {formatBytes(pendingFile.size)}
                </p>
                <StickerUploadFeedback
                  file={pendingFile}
                  info={imageInfo}
                  isAnimatedUpload={isAnimatedUpload}
                />
                {suggestingName ? (
                  <p className="mt-2 text-xs text-nova-muted-foreground">Suggesting a name from the image…</p>
                ) : null}
                {suggestNameError && !stickerName.trim() ? (
                  <p className="mt-2 text-xs text-amber-700">{suggestNameError}</p>
                ) : null}
                {!stickerName.trim() && !suggestingName ? (
                  <p className="mt-2 text-amber-700">
                    Enter a name to enable Add sticker
                    {suggestNameError ? '' : ', or wait for an AI suggestion'}.
                  </p>
                ) : null}
                <p className="mt-2 text-xs text-nova-muted-foreground">
                  Drop another image here to replace, or use Clear.
                </p>
              </div>
            </div>
          ) : null}
        </StickerImageDropZone>
        {uploadError ? (
          <div
            className="rounded-lg border border-red-200 bg-red-50 py-2 px-2.5 text-sm text-red-800"
            role="alert"
          >
            <p className="font-medium">Upload failed</p>
            <p className="mt-1">{uploadError}</p>
          </div>
        ) : null}
      </Card>

      {pack.stickers.length === 0 ? (
        <EmptyState title="No stickers in this pack yet" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pack.stickers.map((sticker, index) => (
            <StickerCard
              key={sticker.id}
              sticker={sticker}
              index={index}
              total={pack.stickers.length}
              saving={
                updateSticker.isPending && updateSticker.variables?.id === sticker.id
              }
              onSave={(name) => updateSticker.mutate({ id: sticker.id, name })}
              onMoveUp={() =>
                updateSticker.mutate({
                  id: sticker.id,
                  sort_order: Math.max(0, index - 1),
                })
              }
              onMoveDown={() =>
                updateSticker.mutate({
                  id: sticker.id,
                  sort_order: index + 1,
                })
              }
              onDelete={() => {
                if (window.confirm(`Delete "${sticker.name}"?`)) {
                  deleteSticker.mutate(sticker.id);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type StickerImageDropZoneProps = {
  disabled?: boolean;
  onFileSelected: (file: File) => void;
  onInvalidFile: () => void;
  onChooseClick: () => void;
  children?: ReactNode;
};

function StickerImageDropZone({
  disabled = false,
  onFileSelected,
  onInvalidFile,
  onChooseClick,
  children,
}: StickerImageDropZoneProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  function handleDragEnter(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) {
      return;
    }
    dragCounterRef.current += 1;
    setIsDragActive(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragActive(false);
    if (disabled) {
      return;
    }

    const file = pickImageFileFromDataTransfer(event.dataTransfer);
    if (!file) {
      onInvalidFile();
      return;
    }
    onFileSelected(file);
  }

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={clsx(
        'rounded-lg border-2 border-dashed p-4 transition-colors',
        disabled && 'cursor-not-allowed opacity-60',
        isDragActive
          ? 'border-nova-primary bg-nova-primary/5'
          : 'border-nova-border bg-nova-muted hover:border-nova-input',
      )}
    >
      {children ?? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-sm text-nova-muted-foreground">
          <p className="font-medium text-nova-foreground">
            {isDragActive ? 'Drop image to upload' : 'Drag and drop an image here'}
          </p>
          <p>or</p>
          <Button type="button" variant="secondary" disabled={disabled} onClick={onChooseClick}>
            Choose image
          </Button>
          <p className="text-xs text-nova-muted-foreground">
            PNG, WebP, JPEG, GIF, or animated PNG (converted to GIF on upload)
          </p>
        </div>
      )}
      {children && isDragActive ? (
        <p className="mt-3 text-center text-xs font-medium text-nova-primary">
          Drop image to replace
        </p>
      ) : null}
    </div>
  );
}

type StickerUploadFeedbackProps = {
  file: File;
  info: ImageInfo | null;
  isAnimatedUpload: boolean;
};

function StickerUploadFeedback({
  file,
  info,
  isAnimatedUpload,
}: StickerUploadFeedbackProps) {
  const warnings = getStickerWarnings(file, info, isAnimatedUpload);

  if (warnings.length === 0) {
    if (!info) {
      return null;
    }
    return (
      <p className="mt-2 text-xs text-green-700">
        Looks good — square, at least {RECOMMENDED_DIMENSION}px, and under{' '}
        {formatBytes(MAX_RECOMMENDED_BYTES)}.
      </p>
    );
  }

  return (
    <ul className="mt-2 space-y-1">
      {warnings.map((warning) => (
        <li key={warning} className="text-xs text-amber-700">
          ⚠ {warning}
        </li>
      ))}
    </ul>
  );
}

type PackSettingsCardProps = {
  pack: AdminStickerPack;
  saving: boolean;
  onSave: (body: Partial<Pick<AdminStickerPack, 'name' | 'slug' | 'sort_order' | 'is_published'>>) => void;
  onRequestDelete: () => void;
};

function PackSettingsCard({ pack, saving, onSave, onRequestDelete }: PackSettingsCardProps) {
  const [name, setName] = useState(pack.name);
  const [slug, setSlug] = useState(pack.slug);
  const [sortOrder, setSortOrder] = useState(String(pack.sort_order));
  const [isPublished, setIsPublished] = useState(pack.is_published);

  useEffect(() => {
    setName(pack.name);
    setSlug(pack.slug);
    setSortOrder(String(pack.sort_order));
    setIsPublished(pack.is_published);
  }, [pack.id, pack.name, pack.slug, pack.sort_order, pack.is_published]);

  const sortOrderNum = Number(sortOrder);
  const canSave =
    name.trim().length > 0 &&
    slug.trim().length > 0 &&
    Number.isFinite(sortOrderNum) &&
    (name.trim() !== pack.name ||
      slug.trim() !== pack.slug ||
      sortOrderNum !== pack.sort_order ||
      isPublished !== pack.is_published);

  function handleSave() {
    onSave({
      name: name.trim(),
      slug: slug.trim(),
      sort_order: sortOrderNum,
      is_published: isPublished,
    });
  }

  return (
    <Card className="space-y-4">
      <h2 className="text-sm font-medium text-nova-foreground">Pack settings</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="text-nova-muted-foreground">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
        </label>
        <label className="block text-sm">
          <span className="text-nova-muted-foreground">Slug</span>
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} className="mt-1" />
        </label>
        <label className="block text-sm">
          <span className="text-nova-muted-foreground">Sort order</span>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="mt-1"
          />
        </label>
        <label className="flex items-center gap-2 pt-6 text-sm">
          <input
            type="checkbox"
            checked={isPublished}
            onChange={(e) => setIsPublished(e.target.checked)}
          />
          <span>Published (visible in app)</span>
        </label>
      </div>
      <p className="text-xs text-nova-muted-foreground">
        New packs start as drafts. Check Published and save when you are ready for the app to
        show this pack.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" disabled={!canSave || saving} loading={saving} onClick={handleSave}>
          Save settings
        </Button>
        <Button type="button" variant="danger" size="sm" onClick={onRequestDelete}>
          Delete pack
        </Button>
      </div>
    </Card>
  );
}

type DeletePackDialogProps = {
  packName: string;
  stickerCount: number;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function PublishPushDialog({
  packName,
  onClose,
}: {
  packName: string;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('New Stickers!');
  const [body, setBody] = useState(`"${packName}" stickers are now here 🖼️`);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const stickersPath = pushDeeplinkPath('stickers');

  const broadcast = useMutation({
    mutationFn: () => {
      const data = pushDataFromDeeplink('stickers');
      return apiJson<CreatePushBroadcastRes>('/v1/admin/push-broadcasts', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim() || 'New Stickers!',
          body: body.trim() || `"${packName}" stickers are now here 🖼️`,
          ...(data ? { data } : {}),
        }),
      });
    },
    onSuccess: (res) => {
      setActiveJobId(res.job.id);
    },
  });

  const jobQ = useQuery({
    queryKey: ['admin', 'push-broadcast', activeJobId],
    queryFn: () => apiJson<PushBroadcastJob>(`/v1/admin/push-broadcasts/${activeJobId}`),
    enabled: Boolean(activeJobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status || status === 'completed' || status === 'failed') {
        return false;
      }
      return 2000;
    },
  });

  const activeJob = jobQ.data ?? null;
  const isRunning =
    Boolean(activeJobId) &&
    (!activeJob || activeJob.status === 'pending' || activeJob.status === 'running');
  const isFinished =
    activeJob?.status === 'completed' || activeJob?.status === 'failed';
  const fieldsLocked = broadcast.isPending || Boolean(activeJobId);

  function handleBackdropClick() {
    if (isRunning || broadcast.isPending) return;
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-push-title"
        className="w-full max-w-lg rounded-lg bg-nova-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="publish-push-title" className="text-lg font-semibold text-nova-foreground">
          Notify users?
        </h2>
        <p className="mt-2 text-sm text-nova-muted-foreground">
          <span className="font-medium text-nova-foreground">{packName}</span> is now published. Send a
          push notification to all push-ready users?
        </p>

        <div className="mt-4 space-y-3">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={fieldsLocked}
          />
          <label className="block text-sm">
            <span className="text-nova-muted-foreground">Body</span>
            <textarea
              className="mt-1 w-full min-h-[72px] rounded-lg border border-nova-border py-2 px-2.5 text-sm disabled:bg-nova-muted disabled:text-nova-muted-foreground"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={fieldsLocked}
            />
          </label>
          <p className="text-xs text-nova-muted-foreground">
            Opens Sticker Packs
            {stickersPath ? (
              <>
                {' '}
                (<span className="font-mono">{stickersPath}</span>)
              </>
            ) : null}
            .
          </p>
        </div>

        {broadcast.error ? (
          <p className="mt-3 text-sm text-red-600">
            {getApiErrorMessage(broadcast.error, 'Could not start broadcast.')}
          </p>
        ) : null}

        {activeJob ? (
          <div className="mt-4">
            <BroadcastJobStatusPanel job={activeJob} />
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          {isFinished ? (
            <Button type="button" size="sm" onClick={onClose}>
              Done
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={isRunning || broadcast.isPending}
                onClick={onClose}
              >
                Skip
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                loading={broadcast.isPending || isRunning}
                disabled={fieldsLocked}
                onClick={() => void broadcast.mutate()}
              >
                Send notification
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DeletePackDialog({
  packName,
  stickerCount,
  deleting,
  onCancel,
  onConfirm,
}: DeletePackDialogProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-pack-title"
        className="w-full max-w-md rounded-lg bg-nova-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="delete-pack-title" className="text-lg font-semibold text-nova-foreground">
          Delete sticker pack?
        </h2>
        <p className="mt-2 text-sm text-nova-muted-foreground">
          <span className="font-medium text-nova-foreground">{packName}</span> and all{' '}
          {stickerCount} sticker{stickerCount === 1 ? '' : 's'} will be permanently removed.
          This cannot be undone.
        </p>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" disabled={deleting} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" variant="danger" size="sm" loading={deleting} onClick={onConfirm}>
            Delete pack
          </Button>
        </div>
      </div>
    </div>
  );
}

type StickerCardProps = {
  sticker: AdminStickerPack['stickers'][number];
  index: number;
  total: number;
  saving: boolean;
  onSave: (name: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
};

function StickerCard({
  sticker,
  index,
  total,
  saving,
  onSave,
  onMoveUp,
  onMoveDown,
  onDelete,
}: StickerCardProps) {
  const [name, setName] = useState(sticker.name);

  useEffect(() => {
    setName(sticker.name);
  }, [sticker.name]);

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && trimmed !== sticker.name;

  return (
    <Card className="space-y-3">
      <img
        src={sticker.image_url}
        alt={sticker.name}
        className="mx-auto h-32 w-32 rounded-lg object-contain"
        style={CHECKERBOARD_STYLE}
      />
      <label className="block text-sm">
        <span className="text-nova-muted-foreground">Name</span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1"
        />
      </label>
      <Button
        type="button"
        size="sm"
        disabled={!canSave || saving}
        loading={saving}
        onClick={() => onSave(trimmed)}
      >
        Save name
      </Button>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" disabled={index === 0} onClick={onMoveUp}>
          ↑
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={index === total - 1}
          onClick={onMoveDown}
        >
          ↓
        </Button>
        <Button variant="secondary" size="sm" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </Card>
  );
}
